import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MessageBar, MessageBarType, PrimaryButton, Spinner, Stack, Text } from "@fluentui/react";
import useAxios from "axios-hooks";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import { Form, Formik } from "formik";
import BenefitFormModal from "./BenefitFormModal";
import BenefitDeleteDialog from "./BenefitDeleteDialog";
import BenefitList from "./BenefitList";
import BenefitsHubSettingsForm from "./BenefitsHubSettingsForm";
import Toast from "../../../Forms/Toast";
import FeatureSummary from "../../../Util/FeatureSummary";

const CustomMenuList = ({ children, ...props }) => {
  const height = 35;
  const itemCount = children.length;
  const totalHeight = Math.min(300, itemCount * height);

  return (
    <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
      {({ index, style }) => (
        <div
          style={{
            ...style,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "0 8px",
            boxSizing: "border-box",
          }}
          title={children[index]?.props?.children}
        >
          {children[index]}
        </div>
      )}
    </List>
  );
};

const getInitialBenefit = (cobrandId) => ({
  benefitId: null,
  cobrandId,
  benefitCategoryId: null,
  benefitTypeId: null,
  providerName: "",
  thumbnailUrl: "",
  benefitDetail: "",
  callToActionId: 0,
  featuredOrder: "",
});

const sortBenefits = (benefits) => {
  return [...benefits].sort((a, b) => {
    const orderA = a.featuredOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.featuredOrder ?? Number.MAX_SAFE_INTEGER;

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    const providerA = (a.providerName || "").toLowerCase();
    const providerB = (b.providerName || "").toLowerCase();
    return providerA.localeCompare(providerB);
  });
};

const BenefitsPage = () => {
  const [selectedCobrandId, setSelectedCobrandId] = useState(null);
  const [benefitsData, setBenefitsData] = useState(null);
  const [formState, setFormState] = useState({ isOpen: false, benefit: null });
  const [deleteState, setDeleteState] = useState({ isOpen: false, benefit: null });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("Benefit saved successfully.");
  const [hubSettings, setHubSettings] = useState(null);

  const [{ data: cobrands, loading: loadingCobrands }] = useAxios({ url: "/internal/cobrands" });
  const [{ loading: loadingAdminData, error: adminError }, fetchAdminData] = useAxios(
    { url: "/benefits/admin/cobrand/0", method: "GET" },
    { manual: true }
  );
  const [{ loading: savingBenefit }, upsertBenefit] = useAxios(
    { url: "/benefits/admin", method: "POST" },
    { manual: true }
  );
  const [{ loading: deletingBenefit }, deleteBenefit] = useAxios(
    { method: "DELETE" },
    { manual: true }
  );
  const [{ loading: loadingHubSettings, error: hubSettingsError }, fetchHubSettings] = useAxios(
    { method: "GET" },
    { manual: true }
  );
  const [{ loading: savingHubSettings }, upsertHubSettings] = useAxios(
    { url: "/benefits/admin/settings", method: "POST" },
    { manual: true }
  );

  const cobrandOptions = useMemo(
    () =>
      (cobrands || []).map((c) => ({
        value: c.cobrandId,
        label: c.name,
      })),
    [cobrands]
  );

  const selectedCobrandOption = useMemo(
    () => cobrandOptions.find((option) => option.value === selectedCobrandId) || null,
    [cobrandOptions, selectedCobrandId]
  );

  const loadBenefits = useCallback(
    async (cobrandId) => {
      if (!cobrandId) {
        setBenefitsData(null);
        return;
      }

      const result = await fetchAdminData({ url: `/benefits/admin/cobrand/${cobrandId}` });
      if (!result?.error) {
        const incoming = result.data || {};
        const benefits = sortBenefits(incoming.benefits || []);
        setBenefitsData({ ...incoming, benefits });
      }
    },
    [fetchAdminData]
  );

  const loadHubSettings = useCallback(
    async (cobrandId) => {
      if (!cobrandId) {
        setHubSettings(null);
        return;
      }

      const result = await fetchHubSettings({ url: `/benefits/settings/${cobrandId}` });
      if (!result?.error) {
        setHubSettings(result.data || null);
      }
    },
    [fetchHubSettings]
  );

  useEffect(() => {
    if (selectedCobrandId) {
      loadBenefits(selectedCobrandId);
      loadHubSettings(selectedCobrandId);
    } else {
      setBenefitsData(null);
      setHubSettings(null);
    }
  }, [selectedCobrandId, loadBenefits, loadHubSettings]);

  const benefits = benefitsData?.benefits || [];
  const benefitCategories = benefitsData?.benefitCategories || [];
  const benefitTypes = benefitsData?.benefitTypes || [];
  const callToActions = benefitsData?.callToActions || [];

  const handleAdd = () => {
    if (!selectedCobrandId) {
      return;
    }
    setFormState({ isOpen: true, benefit: getInitialBenefit(selectedCobrandId) });
  };

  const handleEdit = (benefit) => {
    setFormState({ isOpen: true, benefit: {
      ...benefit,
      callToActionId: benefit.callToActionId || 0,
      featuredOrder: benefit.featuredOrder === null || benefit.featuredOrder === undefined ? "" : benefit.featuredOrder.toString(),
    } });
  };

  const handleDelete = (benefit) => {
    setDeleteState({ isOpen: true, benefit });
  };

  const closeForm = () => setFormState({ isOpen: false, benefit: null });
  const closeDelete = () => setDeleteState({ isOpen: false, benefit: null });

  const handleSave = async (values) => {
    const payload = {
      ...values,
      benefitId: values.benefitId,
      cobrandId: selectedCobrandId,
      featuredOrder:
        values.featuredOrder === "" || values.featuredOrder === null ? null : Number(values.featuredOrder),
      callToActionId: values.callToActionId === 0 ? null : values.callToActionId,
    };

    const result = await upsertBenefit({ data: payload });

    if (!result?.error && result?.data) {
      setBenefitsData((prev) => {
        const current = prev || { benefits: [], benefitCategories, benefitTypes, callToActions };
        const existing = current.benefits || [];
        const updated = existing.filter((b) => b.benefitId !== result.data.benefitId);
        updated.push(result.data);
        return {
          ...current,
          benefits: sortBenefits(updated),
        };
      });
      setToastMessage("Benefit saved successfully.");
      setShowToast(true);
      closeForm();
    }

    return result;
  };

  const handleConfirmDelete = async () => {
    if (!deleteState.benefit) {
      return;
    }

    const result = await deleteBenefit({
      url: `/benefits/admin/${deleteState.benefit.benefitId}/${selectedCobrandId}`,
    });

    if (!result?.error) {
      setBenefitsData((prev) => {
        if (!prev) {
          return prev;
        }
        const updated = (prev.benefits || []).filter(
          (benefit) => benefit.benefitId !== deleteState.benefit.benefitId
        );
        return {
          ...prev,
          benefits: sortBenefits(updated),
        };
      });
      setToastMessage("Benefit deleted successfully.");
      setShowToast(true);
      closeDelete();
    }
  };

  return (
    <Stack tokens={{ childrenGap: 16 }} style={{ maxWidth: 1200 }}>
      <Text variant="xLarge">Benefits</Text>
      <div style={{ maxWidth: 400 }}>
        <Text variant="mediumPlus" styles={{ root: { marginBottom: 8, display: "block" } }}>
          Select Cobrand
        </Text>
        <Select
          isDisabled={loadingCobrands}
          options={cobrandOptions}
          onChange={(option) => setSelectedCobrandId(option ? option.value : null)}
          value={selectedCobrandOption}
          placeholder="Select a cobrand"
          isSearchable
          components={{ MenuList: CustomMenuList }}
        />
      </div>

      {selectedCobrandId && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="space-between">
            <PrimaryButton text="Add Benefit" onClick={handleAdd} disabled={loadingAdminData || savingBenefit} />
          </Stack>

          {adminError && (
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              There was a problem loading benefits data. Please try again.
            </MessageBar>
          )}

          {loadingAdminData ? (
            <Spinner label="Loading benefits..." />
          ) : (
            <BenefitList
              benefits={benefits}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
        </Stack>
      )}

      <BenefitFormModal
        isOpen={formState.isOpen}
        onDismiss={closeForm}
        onSave={handleSave}
        saving={savingBenefit}
        benefit={formState.benefit}
        categories={benefitCategories}
        types={benefitTypes}
        callToActions={callToActions}
      />

      <BenefitDeleteDialog
        isOpen={deleteState.isOpen}
        onDismiss={closeDelete}
        onConfirm={handleConfirmDelete}
        deleting={deletingBenefit}
        benefit={deleteState.benefit}
      />

      {selectedCobrandId && (
        <Stack tokens={{ childrenGap: 12 }} style={{ marginTop: 32, maxWidth: 800 }}>
          <Text variant="xLarge">Benefits Hub Settings</Text>
          
          {hubSettingsError && (
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              There was a problem loading benefits hub settings. Please try again.
            </MessageBar>
          )}

          {loadingHubSettings ? (
            <Spinner label="Loading hub settings..." />
          ) : (
            <Formik
              enableReinitialize
              initialValues={{
                benefitsHubHeroHeader: hubSettings?.benefitsHubHeroHeader || "",
                benefitsHubHeroHeaderAccent: hubSettings?.benefitsHubHeroHeaderAccent || "",
                benefitsHubHeroHeaderEnd: hubSettings?.benefitsHubHeroHeaderEnd || "",
                benefitsHubHeroSubHeader: hubSettings?.benefitsHubHeroSubHeader || "",
                benefitsHubHeroImage: hubSettings?.benefitsHubHeroImage || "",
                benefitsHubFeaturedSubHeading: hubSettings?.benefitsHubFeaturedSubHeading || "",
              }}
              onSubmit={async (values) => {
                const payload = {
                  coBrandId: selectedCobrandId,
                  ...values,
                };

                const result = await upsertHubSettings({ data: payload });

                if (!result?.error && result?.data) {
                  setHubSettings(result.data);
                  setToastMessage("Benefits Hub Settings saved successfully.");
                  setShowToast(true);
                }
              }}
            >
              {({ dirty, isSubmitting }) => (
                <Form>
                  <BenefitsHubSettingsForm />
                  <div style={{ marginTop: 16 }}>
                    <PrimaryButton
                      type="submit"
                      text="Save Hub Settings"
                      disabled={!dirty || isSubmitting || savingHubSettings}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </Stack>
      )}

      <Toast show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
      <FeatureSummary
          label="About Benefits Management"
          summary="Manage a benefits page for a cobrand. Add, edit, or delete benefits including provider details, categories, thumbnails, and featured ordering. Also configure Benefits Hub settings like hero images and featured subheadings. Generally, if turned on for a cobrand, it can be found at '/benefits/all' on the public site. The link is generally accessible from the footer if enabled."
          imageUrlPath="benefits-footer"
      />
    </Stack>
  );
};

export default BenefitsPage;
